import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSocket } from "../hooks/useSocket";

interface FirstBloodEvent {
  challengeTitle: string;
  username: string;
}

export default function FirstBloodOverlay() {
  const [event, setEvent] = useState<FirstBloodEvent | null>(null);
  const { on, off } = useSocket();

  const handleFirstBlood = useCallback((data: FirstBloodEvent) => {
    setEvent(data);
  }, []);

  useEffect(() => {
    on("first-blood", handleFirstBlood);
    return () => {
      off("first-blood", handleFirstBlood);
    };
  }, [on, off, handleFirstBlood]);

  useEffect(() => {
    if (!event) return;
    const timer = setTimeout(() => setEvent(null), 2000);
    return () => clearTimeout(timer);
  }, [event]);

  return (
    <AnimatePresence>
      {event && (
        <motion.div
          key="first-blood-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ds-bg/90"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="relative flex flex-col items-center gap-6 px-8"
          >
            <div className="relative h-32 w-32">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="absolute inset-0 rounded-full border-2 border-ds-coral/40"
                  initial={{ scale: 0.3, opacity: 0.8 }}
                  animate={{ scale: 1.6, opacity: 0 }}
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    delay: i * 0.6,
                    ease: "easeOut",
                  }}
                />
              ))}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{
                    boxShadow: [
                      "0 0 20px 8px rgba(255,107,92,0.2)",
                      "0 0 40px 16px rgba(255,107,92,0.35)",
                      "0 0 20px 8px rgba(255,107,92,0.2)",
                    ],
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-ds-coral/20"
                >
                  <svg
                    className="h-7 w-7 text-ds-coral"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61z" />
                  </svg>
                </motion.div>
              </div>
            </div>

            <div className="text-center">
              <motion.p
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="mb-2 font-mono text-xs uppercase tracking-widest text-ds-coral"
              >
                First Blood
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="mb-1 text-lg font-semibold text-ds-text"
              >
                {event.challengeTitle}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="text-sm text-ds-text-dim"
              >
                solved by{" "}
                <span className="font-medium text-ds-accent">
                  {event.username}
                </span>
              </motion.p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
